import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  input,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { Config } from '@environments/config';
import { Assistants, Chats, Downloads, Resources, Search } from '@local/client-contracts';
import { Command } from '@local/client-contracts/src/commands';
import { ExternalSourceItem } from '@local/client-contracts/src/search';
import { isExtension } from '@local/common-web';
import { TypingViewMode, UTypingTypographyComponent } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DownloadsService } from '@shared/services/downloads.service';
import { windowSizeObserver } from '@shared/utils';
import { stopEvent } from '@shared/utils/elements-util';
import { isEmpty, isEqual, keyBy } from 'lodash';
import { filter, Subscription } from 'rxjs';
import { AnswerSearchService } from 'src/app/bar/services/answers-search.service';
import { ResultMarkdownService } from 'src/app/bar/services/result-markdown.service';
import { buildResourceLookupFromJson } from 'src/app/bar/utils/formatting-utils';
import { FilePreviewService } from '../../../preview/file-preview/services/file-preview.service';
import { AnswerActionsComponent } from '../../../results/components/answer-actions/answer-actions.component';
import { AnswerConstants } from '../../../results/components/result-answer-item/answer.constants';
import { AssistantChatData } from '../../model';
import { ChatResourcesService } from '../../services/chat-resources.service';

@UntilDestroy()
@Component({
  selector: 'chat-answer-message',
  templateUrl: './chat-answer-message.component.html',
  styleUrls: ['../../../results/components/result-answer-item/base-answer.scss', './chat-answer-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatAnswerMessageComponent implements OnDestroy, AfterViewInit {
  private _message: Chats.AssistantMessage;
  private resourcesSub: Subscription;
  readonly answerActionsWidth: number = 124;
  readonly assistantIcon = { 'font-size': '12px', height: '16px', width: '16px' };
  readonly assistantIconExtension = { 'font-size': '19px', height: '24px', width: '24px' };

  private sandboxLinkListeners: { element: HTMLElement; listener: EventListener }[] = [];

  textContent: string;
  resources: Search.ResultItem[];
  isHovered = false;
  isAnswerResultsAvailable: boolean;
  isAnyActionOpen = signal<boolean>(false);
  resultsWidth: number;
  resourcePopupActiveIndex: number;
  typing: WritableSignal<boolean> = signal(true);
  isExtension = isExtension();

  messageInprogress = input<boolean>();

  @Input() assistantData: AssistantChatData;
  @Input() sessionId: string;
  @Input() questionQuery: string;
  @Input() searchId: string;
  @Input() isLoadingAnswer: boolean;
  @Input() isFeedbackOpen: boolean;
  @Input() viewMode: TypingViewMode = 'interactive';
  @Input() chatId: string;
  @Input() showSearchId: boolean;
  @Input() set message(ans: Chats.AssistantMessage) {
    if (isEqual(ans, this._message)) return;
    this._message = ans;
    this.textContent = this.getAnswerText(this._message);
    this.updateResources(ans);
    this.isAnswerResultsAvailable = ans.state === Chats.AssistantMessageState.ResultsAvailable;
    this.cdr.markForCheck();
  }

  get message(): Chats.AssistantMessage {
    return this._message;
  }

  @Output() answerTypedFinished = new EventEmitter<void>();
  @Output() feedbackShow = new EventEmitter<{
    resources: Search.ResultItem[];
    intent: Assistants.QuestionIntent;
    type: Resources.FeedbackType;
    searchId: string;
  }>();

  @ViewChild('chatAnswer') chatAnswerRef: ElementRef;
  @ViewChild('answerText') answerTextRef: UTypingTypographyComponent;
  @ViewChild('footerAnswer') footerAnswerRef: ElementRef;

  @ViewChild(AnswerActionsComponent) answerActionsComponent: AnswerActionsComponent;

  @HostListener('contextmenu', ['$event'])
  async onRightClick(event: MouseEvent) {
    const selectedText = window.getSelection().toString();
    if (selectedText) {
      this.answerActionsComponent?.openContextMenu(event, selectedText);
      this.isAnyActionOpen.set(true);
      stopEvent(event);
    }
  }

  @HostBinding('attr.isExtensionMode')
  get isExtensionMode() {
    return this.isExtension;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private chatResourcesService: ChatResourcesService,
    private resultMarkdownService: ResultMarkdownService,
    private filePreviewService: FilePreviewService, // for instancing purposes
    private answerSearchService: AnswerSearchService,
    protected downloadsService: DownloadsService
  ) {
    this.answerSearchService.resourcePopupOpen$
      .pipe(
        filter((res) => res?.id === this.message?.searchId),
        untilDestroyed(this)
      )
      .subscribe((res) => {
        this.resourcePopupActiveIndex = res?.index;
        this.cdr.markForCheck();
      });
    effect(() => {
      const messageInprogress = this.messageInprogress();
      if (!messageInprogress) {
        this.addSandboxLinkListeners();
      }
    });
  }

  ngAfterViewInit() {
    this.initResultsWidth();
  }

  ngOnDestroy() {
    if (this.resourcesSub) {
      this.resourcesSub.unsubscribe();
    }
    this.removeSandboxLinkListeners();
  }

  private addSandboxLinkListeners() {
    this.removeSandboxLinkListeners();
    const sandboxLinks = this.answerTextRef?.staticContainerRef()?.nativeElement.querySelectorAll('.sandbox-link');
    if (!sandboxLinks?.length) {
      return;
    }
    const record: Record<string, Assistants.QuestionFileItem> = keyBy(this.message.downloadFiles, (item) => item.metadata.originalUrl);
    sandboxLinks.forEach((link: HTMLAnchorElement) => {
      const listener = (event: Event) => {
        stopEvent(event);
        const sandboxPath = link.getAttribute('data-sandbox-path');
        if (sandboxPath) {
          this.handleSandboxLink(sandboxPath, record);
        }
      };

      link.addEventListener('click', listener);
      this.sandboxLinkListeners.push({ element: link, listener });
    });
  }

  private removeSandboxLinkListeners() {
    if (!this.sandboxLinkListeners?.length) {
      return;
    }
    this.sandboxLinkListeners.forEach(({ element, listener }) => {
      element.removeEventListener('click', listener);
    });

    this.sandboxLinkListeners = [];
  }

  private async handleSandboxLink(originalUrl: string, record: Record<string, Assistants.QuestionFileItem>) {
    if (isEmpty(record)) {
      return;
    }
    const fileId = record[originalUrl]?.fileId;
    if (!fileId) {
      return;
    }
    const request: Downloads.UrlDownloadRequest = {
      type: 'Url',
      url: this.answerSearchService.getFileDownloadUrl(fileId, this.chatId, this.assistantData.id),
      grantType: 'llm:assistants:files',
    };
    await this.downloadsService.urlDownload(request, 'visible');
  }
  private initResultsWidth() {
    setTimeout(() => {
      windowSizeObserver()
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.resultsWidth = this.chatAnswerRef?.nativeElement?.offsetWidth;
          this.cdr.markForCheck();
        });
    }, 0);
  }

  onTypingFinished() {
    if (this.message.intent === 'ResourceLookup' && this.message.state === Chats.AssistantMessageState.ResultsAvailable) {
      this.typing.set(!this.typing());
    } else {
      this.onTypedComplete();
    }
  }

  onTypedComplete() {
    this.answerTypedFinished.emit();
    this.cdr.markForCheck();
  }

  toggleHover(isHovered: boolean): void {
    this.isHovered = isHovered;
  }

  private updateResources(message: Chats.AssistantMessage) {
    this.updateInternalResources(message);
    this.updateExternalResources(message);
  }

  private updateInternalResources(message: Chats.AssistantMessage) {
    if (this.resourcesSub) {
      this.resourcesSub.unsubscribe();
    }
    const ids = message.resources?.map((r) => r?.externalId);
    if (ids?.length) {
      this.resourcesSub = this.chatResourcesService
        .getResourcesByIds$(ids)
        .pipe(untilDestroyed(this))
        .subscribe((res) => {
          this.resources = message.resources
            ?.map((messageResource) => {
              const verificationStatus = messageResource.verificationStatus;
              const resource = res.find((r) => r?.resource?.externalId === messageResource.externalId);
              if (resource) {
                resource.resource.data.verificationStatus = verificationStatus;
              }
              return resource;
            })
            .filter((r) => !!r);
          this.cdr.markForCheck();
        });
    }
  }

  private updateExternalResources(message: Chats.AssistantMessage) {
    if (message.dataSources) {
      this.resources = message.dataSources.map((source) => this.buildExternalResource(source));
    }
  }

  private buildExternalResource(source: ExternalSourceItem): ExternalSourceItem {
    return {
      type: 'external-source',
      ...source,
      view: {
        linkName: source.siteName,
        title: {
          text: source.title || source['name'],
          onClick: {
            url: source.url,
            type: 'open-url',
          } as Command,
        },
        subtitle: {
          text: source.description,
        },
        icon: {
          lightUrl: source.icon,
        },
      },
    };
  }

  private getEmptyResultMsg(): string {
    return this.message?.intent === 'ResourceLookup' ? AnswerConstants.NO_ANSWER_TEXT_LOOK_UP : AnswerConstants.NO_ANSWER_TEXT;
  }

  private getAnswerText(message: Chats.AssistantMessage): string {
    if (!message?.state) {
      return AnswerConstants.NO_ANSWER_TEXT;
    }
    switch (message.state) {
      case Chats.AssistantMessageState.ResultsAvailable:
      case Chats.AssistantMessageState.QueryCancelledWithResult:
        return this.getMessageWithResults(message);
      case Chats.AssistantMessageState.NoResultsFound:
        return this.getEmptyResultMsg();
      case Chats.AssistantMessageState.QueryCancelled:
        return AnswerConstants.ANSWER_HALTED_MESSAGE;
      case Chats.AssistantMessageState.RephraseRequired:
        return AnswerConstants.REPHRASE_REQUIRED_MESSAGE;
      default:
        return this.getEmptyResultMsg();
    }
  }

  private getMessageWithResults(message: Chats.AssistantMessage): string {
    let textToFormatted = message.content || '';
    if (message.intent === 'ResourceLookup') {
      textToFormatted = buildResourceLookupFromJson(message.resources, textToFormatted);
    }
    if (message?.references?.length) {
      textToFormatted = this.answerSearchService.insertReferencesIntoText(
        {
          answer: message.content,
          references: message.references,
          resourceIds: message.resourceIds || message.resources.map((item) => item.resourceId),
        },
        message.resources
      );
    }

    if (this.assistantData?.knowledgeType === 'External') {
      textToFormatted = textToFormatted.replace(/【.*?】/g, '');
    }

    return this.resultMarkdownService.render(
      textToFormatted,
      message?.references?.length
        ? { items: message.resources, sourceType: 'chat-answer', identity: message.searchId, searchId: message.searchId }
        : null
    );
  }

  onShowFeedback(type: Resources.FeedbackType) {
    this.feedbackShow.emit({ resources: this.resources, intent: this.message.intent, type, searchId: this.message.searchId });
  }

  openSearchLog(searchId: string) {
    window.open(`${Config.downloadSearchLogUrl}${encodeURIComponent(searchId)}&redirect=true`, '_blank');
  }
}
